import React from "react"
import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material"
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
} from "@mui/icons-material"
import styles from "./CandidateProfile.module.css"
// import StaffRatingModal from "./StaffRatingModal"
import StaffRatingModal from "./StaffRatingModal"

interface CandidateProfileProps {
  open: boolean
  onClose: () => void
  candidate: any
  onSendEmail: (candidate: any) => void
  onUpdateStatus: (candidateId: string, status: string) => void
  project?: any
  onRateStaff?: (ratingData: any) => Promise<void>
}

const CandidateProfile: React.FC<CandidateProfileProps> = ({
  open,
  onClose,
  candidate,
  onSendEmail,
  onUpdateStatus,
  project,
  onRateStaff,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [ratingModalOpen, setRatingModalOpen] = useState(false)

  if (!candidate) return null

  const getStatusInfo = (status: any) => {
    if (status.employed === "1" || status.employed === 1) {
      return { label: "Hired", color: "success" as const }
    } else if (status.interviewed === "1" || status.interviewed === 1) {
      return { label: "Interviewed", color: "warning" as const }
    } else if (status.screened === "1" || status.screened === 1) {
      return { label: "Screened", color: "info" as const }
    }
    return { label: "Applied", color: "default" as const }
  }

  const statusInfo = getStatusInfo(candidate.status)
  const isHired = candidate.status.employed === 1 || candidate.status.employed === "1"

  const handleRateStaff = async (ratingData: any) => {
    if (onRateStaff) {
      await onRateStaff(ratingData)
    }
  }

  const formatSalary = (min: string, max: string) => {
    const minNum = Number.parseFloat(min)
    const maxNum = Number.parseFloat(max)
    return `₦${minNum.toLocaleString()} - ₦${maxNum.toLocaleString()}`
  }

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index} className={styles.tabPanel}>
      {value === index && children}
    </div>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className={styles.profileDialog}>
      <DialogTitle className={styles.dialogTitle}>
        <Box className={styles.titleHeader}>
          <Box className={styles.candidateHeader}>
            <Avatar
              className={styles.profileAvatar}
              src={`/placeholder.svg?height=100&width=100&query=professional headshot of ${candidate.full_name}`}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" className={styles.candidateName}>
                {candidate.full_name}
              </Typography>
              <Typography variant="body1" className={styles.candidateTitle}>
                {candidate.preferred_job_titles || "Job Seeker"}
              </Typography>
              <Chip {...statusInfo} className={styles.statusChip} />
            </Box>
          </Box>
          <IconButton onClick={onClose} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Box className={styles.tabsContainer}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} className={styles.tabs}>
            <Tab label="Overview" />
            <Tab label="Experience & Skills" />
            <Tab label="Preferences" />
            <Tab label="Documents" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Contact Information
                    </Typography>
                    <List className={styles.infoList}>
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon />
                        </ListItemIcon>
                        <ListItemText primary="Email" secondary={candidate.email_address} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon />
                        </ListItemIcon>
                        <ListItemText primary="Phone" secondary={candidate.phone_number} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <LocationOnIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Location"
                          secondary={`${candidate.location_area}, ${candidate.location_city}, ${candidate.location_state_region}, ${candidate.location_country}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <FlagIcon />
                        </ListItemIcon>
                        <ListItemText primary="Nationality" secondary={candidate.nationality} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Personal Details
                    </Typography>
                    <List className={styles.infoList}>
                      <ListItem>
                        <ListItemIcon>
                          <CalendarIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Date of Birth"
                          secondary={new Date(candidate.date_of_birth).toLocaleDateString()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText primary="Gender" secondary={candidate.gender} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <LanguageIcon />
                        </ListItemIcon>
                        <ListItemText primary="Languages" secondary={candidate.languages_spoken} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <HomeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Work Authorization" secondary={candidate.work_authorization_status} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Experience & Education
                    </Typography>
                    <List className={styles.infoList}>
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Total Experience"
                          secondary={`${candidate.total_years_experience} years`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SchoolIcon />
                        </ListItemIcon>
                        <ListItemText primary="Education Level" secondary={candidate.highest_education_level} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SchoolIcon />
                        </ListItemIcon>
                        <ListItemText primary="Field of Study" secondary={candidate.fieldOfStudy || "Not specified"} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText primary="Specific Experience" secondary={candidate.specific_roles_experience} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Skills & Functions
                    </Typography>
                    <Box className={styles.skillsContainer}>
                      <Typography variant="body2" className={styles.skillsLabel}>
                        Job Functions:
                      </Typography>
                      <Box className={styles.chipContainer}>
                        {candidate.job_functions?.split(",").map((skill: string, index: number) => (
                          <Chip key={index} label={skill.trim()} size="small" className={styles.skillChip} />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Work Preferences
                    </Typography>
                    <List className={styles.infoList}>
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon />
                        </ListItemIcon>
                        <ListItemText primary="Employment Type" secondary={candidate.employment_type_preferences} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <HomeIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Remote Work"
                          secondary={candidate.remote_work_preference === "1" ? "Yes" : "No"}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <LocationOnIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Willing to Relocate"
                          secondary={candidate.willing_to_relocate === "1" ? "Yes" : "No"}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ScheduleIcon />
                        </ListItemIcon>
                        <ListItemText primary="Availability" secondary={candidate.availability} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className={styles.infoCard}>
                  <CardContent>
                    <Typography variant="h6" className={styles.cardTitle}>
                      Salary & Industry Preferences
                    </Typography>
                    <List className={styles.infoList}>
                      <ListItem>
                        <ListItemIcon>
                          <MoneyIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Salary Expectation"
                          secondary={formatSalary(candidate.salary_expectation_min, candidate.salary_expectation_max)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText primary="Preferred Industries" secondary={candidate.preferred_industries} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon />
                        </ListItemIcon>
                        <ListItemText primary="Preferred Job Titles" secondary={candidate.preferred_job_titles} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Card className={styles.infoCard}>
              <CardContent>
                <Typography variant="h6" className={styles.cardTitle}>
                  Application Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Document management feature coming soon...
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => onSendEmail(candidate)} startIcon={<EmailIcon />}>
            Send Email
          </Button>
          {isHired && onRateStaff && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => setRatingModalOpen(true)}
              startIcon={<StarIcon />}
            >
              Rate Staff
            </Button>
          )}
          <Button variant="contained" onClick={() => onUpdateStatus(candidate.user_id, "screened")}>
            Update Status
          </Button>
        </Stack>
      </DialogActions>

      {isHired && onRateStaff && (
        <StaffRatingModal
          open={ratingModalOpen}
          onClose={() => setRatingModalOpen(false)}
          candidate={candidate}
          project={project}
          onSubmitRating={handleRateStaff}
        />
      )}
    </Dialog>
  )
}

export default CandidateProfile
